import { Module, Commit, ActionTree, MutationTree } from 'vuex';
import {
    PresetCategory,
    PresetTag,
    ImageEffectPreset,
    SPECIAL_CATEGORY_VALUES,
    CategoryValue,
    EffectiveCategory,
    PresetCategoryInfo
} from '@/components/DesignStudio/ImageEffectsProperties/types/presets';
import { applyPresetFilters, PRESET_CATEGORIES } from '@/components/DesignStudio/ImageEffectsProperties/core/presets';
import { ImageEffectType } from '@/typings/customilyElements/imageEffectType';

export interface ImageEffectsPropertiesState {
    search: string;
    searchQuery: string;
    selectedCategory: PresetCategory | undefined;
    selectedTags: PresetTag[];
    showOnlyFree: boolean;
    imageEffect: ImageEffectType | undefined;
    modalAddIsOpen: boolean;
    modalEditIsOpen: boolean;
}

const defaultState: ImageEffectsPropertiesState = {
    search: '',
    searchQuery: '',
    selectedCategory: undefined,
    selectedTags: [],
    showOnlyFree: false,
    imageEffect: undefined,
    modalAddIsOpen: false,
    modalEditIsOpen: false
};

const getters = {
    searchQuery: (state: ImageEffectsPropertiesState) => state.searchQuery,
    selectedCategory: (state: ImageEffectsPropertiesState) => state.selectedCategory,
    selectedTags: (state: ImageEffectsPropertiesState) => state.selectedTags,
    showOnlyFree: (state: ImageEffectsPropertiesState) => state.showOnlyFree,
    hasActiveFilters: (state: ImageEffectsPropertiesState) => {
        return !!(state.searchQuery || state.selectedTags.length || state.showOnlyFree || state.selectedCategory);
    },
    selectedCategoryValue: (state: ImageEffectsPropertiesState) => {
        if (state.showOnlyFree) return SPECIAL_CATEGORY_VALUES.FREE;
        return state.selectedCategory || SPECIAL_CATEGORY_VALUES.ALL;
    },
    isShowingFreeOnly: (state: ImageEffectsPropertiesState) => {
        return state.showOnlyFree;
    },
    effectiveCategory: (state: ImageEffectsPropertiesState) => {
        if (state.showOnlyFree) return SPECIAL_CATEGORY_VALUES.FREE;
        return state.selectedCategory;
    },
    availableTags: (state: ImageEffectsPropertiesState, getters: { effectiveCategory: EffectiveCategory }) => {
        const filteredPresets = applyPresetFilters({
            searchQuery: state.searchQuery,
            effectiveCategory: getters.effectiveCategory,
            selectedTags: state.selectedTags
        });

        const tags = new Set<PresetTag>();
        filteredPresets.forEach((preset) => {
            preset.tags.forEach((tag) => tags.add(tag));
        });
        return Array.from(tags).sort();
    },
    filteredPresets: (state: ImageEffectsPropertiesState, getters: { effectiveCategory: EffectiveCategory }) => {
        return applyPresetFilters({
            searchQuery: state.searchQuery,
            effectiveCategory: getters.effectiveCategory,
            selectedTags: state.selectedTags,
            filterBySelectedTags: true
        });
    },
    groupedFilteredPresets: (
        state: ImageEffectsPropertiesState,
        getters: { filteredPresets: ImageEffectPreset[]; selectedCategoryValue: string }
    ) => {
        const { filteredPresets, selectedCategoryValue } = getters;

        let filteredCategories: PresetCategoryInfo[];
        let filteredPresetsToGroup: ImageEffectPreset[] = filteredPresets;

        if (selectedCategoryValue === SPECIAL_CATEGORY_VALUES.FREE) {
            filteredPresetsToGroup = filteredPresets.filter((preset: ImageEffectPreset) => preset.isFree);
            const freeCategories = new Set(filteredPresetsToGroup.map((preset: ImageEffectPreset) => preset.category));
            filteredCategories = PRESET_CATEGORIES.filter((presetCategory) =>
                freeCategories.has(presetCategory.category)
            );
        } else if (selectedCategoryValue && selectedCategoryValue !== SPECIAL_CATEGORY_VALUES.ALL) {
            filteredCategories = PRESET_CATEGORIES.filter(
                (presetCategory) => presetCategory.category === selectedCategoryValue
            );
        } else {
            filteredCategories = PRESET_CATEGORIES;
        }

        const groups = new Map<PresetCategory, ImageEffectPreset[]>();
        filteredPresetsToGroup.forEach((preset: ImageEffectPreset) => {
            if (!groups.has(preset.category)) {
                groups.set(preset.category, []);
            }
            groups.get(preset.category)!.push(preset);
        });

        return filteredCategories
            .map((categoryInfo: PresetCategoryInfo) => ({
                categoryInfo,
                presets: groups.get(categoryInfo.category) || []
            }))
            .filter(
                (group: { categoryInfo: PresetCategoryInfo; presets: ImageEffectPreset[] }) => group.presets.length > 0
            );
    }
};

const mutations: MutationTree<ImageEffectsPropertiesState> = {
    setModalAddIsOpen: (state, payload: boolean) => {
        state.modalAddIsOpen = payload;
    },
    setSearchQuery: (state, payload: string) => {
        state.searchQuery = payload;
    },
    setSelectedCategory: (state, payload: PresetCategory | undefined) => {
        state.selectedCategory = payload;
    },
    setSelectedTags: (state, payload: PresetTag[]) => {
        state.selectedTags = payload;
    },
    setCategoryValue: (state, payload: CategoryValue | undefined) => {
        if (payload === SPECIAL_CATEGORY_VALUES.FREE) {
            state.showOnlyFree = true;
            state.selectedCategory = undefined;
        } else {
            state.showOnlyFree = false;
            state.selectedCategory = payload === SPECIAL_CATEGORY_VALUES.ALL ? undefined : payload;
        }
    },
    toggleTag: (state, tag: PresetTag) => {
        const index = state.selectedTags.indexOf(tag);
        if (index > -1) {
            state.selectedTags.splice(index, 1);
        } else {
            state.selectedTags.push(tag);
        }
    },
    clearFilters: (state) => {
        state.searchQuery = '';
        state.selectedCategory = undefined;
        state.selectedTags = [];
        state.showOnlyFree = false;
    },
    resetModalState: (state) => {
        state.searchQuery = '';
        state.selectedCategory = undefined;
        state.selectedTags = [];
        state.showOnlyFree = false;
        state.imageEffect = undefined;
    },
    addImageEffect: (state, payload: ImageEffectType | undefined) => {
        state.imageEffect = payload;
    }
};

const actions: ActionTree<ImageEffectsPropertiesState, ImageEffectsPropertiesState> = {
    openAddModal({ commit }) {
        commit('setModalAddIsOpen', true);
    },
    closeAddModal({ commit }) {
        commit('setModalAddIsOpen', false);
    },
    updateSearchQuery({ commit }: { commit: Commit }, payload: string) {
        commit('setSearchQuery', payload);
    },
    updateSelectedCategory({ commit }: { commit: Commit }, payload: PresetCategory | undefined) {
        commit('setSelectedCategory', payload);
    },
    updateSelectedTags({ commit }: { commit: Commit }, payload: PresetTag[]) {
        commit('setSelectedTags', payload);
    },
    updateCategoryValue({ commit }: { commit: Commit }, payload: CategoryValue | undefined) {
        commit('setCategoryValue', payload);
    },
    toggleTag({ commit }: { commit: Commit }, tag: PresetTag) {
        commit('toggleTag', tag);
    },
    clearFilters({ commit }: { commit: Commit }) {
        commit('clearFilters');
    },
    resetModalState({ commit }: { commit: Commit }) {
        commit('resetModalState');
    },
    addImageEffect({ commit }: { commit: Commit }, payload: ImageEffectType | undefined) {
        commit('addImageEffect', payload);
    }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const store: Module<ImageEffectsPropertiesState, any> = {
    namespaced: true,
    state: defaultState,
    getters: getters,
    mutations: mutations,
    actions: actions
};
